/**
 * Single source of truth for product main categories / Sistem 1 options.
 * Sistem 1 = categoria produsului (IPA, VMC, Sanitare, etc.).
 */
export const MAIN_PRODUCT_CATEGORIES = [
    { title: 'IPA', value: 'IPA', short: 'IPA', color: 'danger', systemAliases: ['IPA'] },
    { title: 'VMC', value: 'VMC', short: 'VMC', color: '#51C7E0', systemAliases: ['VMC'] },
    { title: 'Tavan', value: 'TAVAN', short: 'Tavan', color: '#3777BB', systemAliases: ['TAVAN'] },
    { title: 'Sanitare', value: 'SANITARE', short: 'Sanit.', color: '#8774B3', systemAliases: ['SANITARE', 'ISA'] },
    {
        title: 'Camera tehnică',
        value: 'CAMERA TEHNICA',
        short: 'CT',
        color: '#8D9B6C',
        systemAliases: ['CT', 'CAMERA TEHNICA'],
    },
    { title: 'Pereți', value: 'PERETI', short: 'Pereți', color: '#F8AC8C', systemAliases: ['PERETI'] },
    {
        title: 'Automatizare',
        value: 'AUTOMATIZARE',
        short: 'Auto',
        color: '#1F4690',
        systemAliases: ['AUTOMATIZARE'],
    },
    {
        title: 'Ventiloconvectoare',
        value: 'VENTILOCONVECTOARE',
        short: 'VC',
        color: '#AB9E9A',
        systemAliases: ['VENTILOCONVECTOARE', 'VC'],
    },
    {
        title: 'Canal termic',
        value: 'CANAL TERMIC',
        short: 'Canal',
        color: '#b269ab',
        systemAliases: ['CANAL TERMIC'],
    },
    { title: 'PDC', value: 'PDC', short: 'PDC', color: '#F37928', systemAliases: ['PDC'] },
    {
        title: 'Fotovoltaice',
        value: 'FOTOVOLTAICE',
        short: 'PV',
        color: '#E6A817',
        systemAliases: ['FOTOVOLTAICE', 'FOTOVOLTAIC', 'PV'],
    },
    { title: 'Aspirare', value: 'ASPIRARE', short: 'Aspir.', color: '#40BFB4', systemAliases: ['ASPIRARE'] },
    {
        title: 'Smart House',
        value: 'SMART HOUSE',
        short: 'SH',
        color: '#4A7C59',
        systemAliases: ['SMART HOUSE', 'SMARTHOUSE', 'SMART HOME', 'SMARTHOME'],
    },
    { title: 'Servicii', value: 'SERVICII', short: 'Serv.', color: '#808080', systemAliases: ['SERVICII'] },
];

/** Options for Sistem 1 dropdown in the product builder. */
export const SYSTEM_OPTIONS = MAIN_PRODUCT_CATEGORIES.map((category) => ({
    title: category.title,
    value: category.value,
}));

/** Maps Sistem 1 (incl. legacy aliases like CT) → DB category name. */
export const SYSTEM_CATEGORY_MAP = MAIN_PRODUCT_CATEGORIES.reduce((map, category) => {
    map[category.value] = category.value;
    for (const alias of category.systemAliases || []) {
        map[alias.toUpperCase()] = category.value;
    }
    return map;
}, {});

export const SYSTEM_DERIVED_CATEGORIES = MAIN_PRODUCT_CATEGORIES.map((category) => category.value);

/**
 * @param {string} system1
 * @returns {typeof MAIN_PRODUCT_CATEGORIES[number]|undefined}
 */
export function findCategoryBySystem(system1) {
    const normalized = String(system1 || '').trim().toUpperCase();
    if (!normalized) {
        return undefined;
    }
    return MAIN_PRODUCT_CATEGORIES.find((category) =>
        category.value === normalized
        || category.systemAliases?.some((alias) => alias.toUpperCase() === normalized)
    );
}

/**
 * @param {string} systemValue
 * @returns {string}
 */
export function normalizeSystemValue(systemValue) {
    return findCategoryBySystem(systemValue)?.value || String(systemValue || '').trim();
}

/** @deprecated Use normalizeSystemValue */
export const normalizeSystem1Value = normalizeSystemValue;

/**
 * @param {string} system1
 * @returns {string|null}
 */
export function mapSystemToCategory(systemValue) {
    const normalized = normalizeSystemValue(systemValue);
    return normalized || null;
}

/**
 * Adds categories for one or more sisteme without removing other selections.
 * @param {string|string[]} systems
 * @param {string[]} categories
 * @returns {string[]}
 */
export function syncCategoriesFromSystems(systems, categories = []) {
    const next = new Set(categories || []);
    const systemList = Array.isArray(systems) ? systems : [systems];
    for (const system of systemList) {
        const mappedCategory = mapSystemToCategory(system);
        if (mappedCategory) {
            next.add(mappedCategory);
        }
    }
    return Array.from(next);
}

/**
 * Adds the category matching a sistem without removing other selected categories.
 * @param {string} system
 * @param {string[]} categories
 * @returns {string[]}
 */
export function syncCategoriesFromSystem(system, categories = []) {
    return syncCategoriesFromSystems(system, categories);
}

/**
 * @param {string} name - DB category name (e.g. IPA, VMC)
 * @returns {string}
 */
export function getCategoryLabel(name) {
    const option = MAIN_PRODUCT_CATEGORIES.find((category) => category.value === name);
    return option?.title || name;
}

/**
 * @param {string} name
 * @returns {string}
 */
export function getCategoryShortLabel(name) {
    const normalized = resolveCategoryName(name);
    const option = MAIN_PRODUCT_CATEGORIES.find((category) => category.value === normalized);
    return option?.short || option?.title || normalized;
}

/**
 * @param {string} name
 * @returns {string}
 */
export function getCategoryColor(name) {
    const normalized = resolveCategoryName(name);
    const option = MAIN_PRODUCT_CATEGORIES.find((category) => category.value === normalized);
    return option?.color || 'primary';
}

const HIDDEN_PRODUCT_CATEGORIES = new Set(['GENERALA']);

/**
 * @param {unknown} value
 * @returns {string}
 */
function resolveCategoryName(value) {
    if (!value) {
        return '';
    }
    if (typeof value === 'object') {
        return String(value.name || value.title || '').trim();
    }
    const asString = String(value).trim();
    // Recover names accidentally persisted as dict/object stringifications.
    if (asString.startsWith('{') && asString.includes('name')) {
        try {
            const parsed = JSON.parse(asString.replace(/'/g, '"'));
            if (parsed?.name) {
                return String(parsed.name).trim();
            }
        } catch (_error) {
            // keep original string
        }
    }
    return asString;
}

/**
 * Main categories assigned to a product, excluding hidden ones, sorted for display.
 * @param {Object} item
 * @returns {string[]}
 */
export function getProductMainCategories(item) {
    if (!item?.category_assignments?.length) {
        return [];
    }

    return [...new Set(
        item.category_assignments
            .map((assignment) => resolveCategoryName(assignment.main_category))
            .filter((name) => name && !HIDDEN_PRODUCT_CATEGORIES.has(name)),
    )].sort((a, b) => getCategoryLabel(a).localeCompare(getCategoryLabel(b), 'ro'));
}

/**
 * @param {Array<{ main_category?: { name?: string }|string, sub_category?: { name?: string }|string }>} categoryAssignments
 * @returns {Record<string, string>}
 */
export function categoryAssignmentsByMainCategory(categoryAssignments = []) {
    const byMain = {};
    for (const assignment of categoryAssignments) {
        const mainCategory = assignment?.main_category?.name || assignment?.main_category;
        const subCategory = assignment?.sub_category?.name || assignment?.sub_category;
        if (!mainCategory || !subCategory) {
            continue;
        }
        byMain[normalizeSystemValue(mainCategory)] = subCategory;
    }
    return byMain;
}

/** Default subsistem for products when none is explicitly assigned. */
export const DEFAULT_SYSTEM_SUBCATEGORY = 'Produse Principale';

/**
 * @param {Array<{ title?: string, value?: string }>} [options]
 * @returns {string}
 */
export function getDefaultSystemSubcategory(options = []) {
    const match = options.find(
        (option) => option.value === DEFAULT_SYSTEM_SUBCATEGORY
            || option.title === DEFAULT_SYSTEM_SUBCATEGORY,
    );
    return match?.value || DEFAULT_SYSTEM_SUBCATEGORY;
}

/**
 * @param {string[]} systems
 * @param {string[]} systemSubcategories
 * @returns {Array<{ main_category: string, sub_category: string }>}
 */
export function buildCategoryAssignmentsFromSystems(systems, systemSubcategories = []) {
    const assignments = [];
    const seenMainCategories = new Set();
    const systemList = Array.isArray(systems) ? systems : [];
    const subcategoryList = Array.isArray(systemSubcategories) ? systemSubcategories : [];

    systemList.forEach((system, index) => {
        const mainCategory = mapSystemToCategory(system);
        if (!mainCategory || seenMainCategories.has(mainCategory)) {
            return;
        }
        // Keep index alignment with the form arrays (do not pre-filter empty systems).
        const subCategory = String(subcategoryList[index] || '').trim() || DEFAULT_SYSTEM_SUBCATEGORY;
        seenMainCategories.add(mainCategory);
        assignments.push({
            main_category: mainCategory,
            sub_category: subCategory,
        });
    });

    return assignments;
}

/**
 * @param {Record<string, unknown>} form
 * @param {Array<{ main_category?: { name?: string }|string, sub_category?: { name?: string }|string }>} [categoryAssignments]
 */
export function hydrateSystemSubcategories(form, categoryAssignments = []) {
    const byMain = categoryAssignmentsByMainCategory(categoryAssignments);
    form.systemSubcategories = (form.systems || []).map((system) => {
        const mainCategory = mapSystemToCategory(system);
        if (!mainCategory) {
            return '';
        }
        return byMain[mainCategory] || DEFAULT_SYSTEM_SUBCATEGORY;
    });
}
